import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Material } from '../../../../classes/material.class';
import { Planning } from '../../../../classes/planning.class';
import { CodaltComponent } from '../../../../codalt.component';
import { PlanningService } from '../../../../services/planning/planning.service';

const CAPACITY_PER_SIDE = 10;

@Component({
    selector: 'app-related-plannings',
    standalone: false,
    templateUrl: './related-plannings.component.html',
    styleUrl: './related-plannings.component.scss',
})
export class RelatedPlanningsComponent extends CodaltComponent implements OnChanges, AfterViewInit {
    @Input() afasProjectId: string;
    @Input() currentPlanningId: number;
    @Input() referenceDate?: Date;
    @Input() materialsById: Map<number, Material>;

    @Output() open = new EventEmitter<Planning>();

    @ViewChild('sectionItems') sectionItemsRef?: ElementRef<HTMLElement>;

    // before/after: dichtstbijzijnde planning bij `currentPlanningId` eerst.
    private before: Planning[] = [];
    private after: Planning[] = [];
    private resizeObserver?: ResizeObserver;

    loading = false;
    current: Planning | null = null;
    beforeItems: Planning[] = [];
    afterItems: Planning[] = [];

    constructor(private planningService: PlanningService) {
        super();
    }

    ngAfterViewInit() {
        this.fitToContainer();

        this.resizeObserver = new ResizeObserver(() => this.fitToContainer());
        if (this.sectionItemsRef) {
            this.resizeObserver.observe(this.sectionItemsRef.nativeElement);
        }
    }

    override ngOnDestroy() {
        this.resizeObserver?.disconnect();
        super.ngOnDestroy();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.afasProjectId) {
            this.load();
        }
    }

    private load() {
        this.beforeItems = [];
        this.afterItems = [];
        this.before = [];
        this.after = [];
        this.current = null;

        if (!this.afasProjectId) {
            return;
        }

        this.loading = true;

        this.subscriptions.add(
            this.planningService
                .getRelatedPlannings(this.afasProjectId, CAPACITY_PER_SIDE * 2, this.referenceDate)
                .subscribe({
                    next: ({ future, history }) => {
                        this.loading = false;
                        this.prepareData(history, future);
                        this.buildWindow();
                        requestAnimationFrame(() => this.fitToContainer());
                    },
                    error: error => {
                        this.loading = false;
                        console.error('Error fetching related plannings:', error);
                    },
                })
        );
    }

    private prepareData(history: Planning[], future: Planning[]) {
        const currentInHistory = history.findIndex(p => p.id === this.currentPlanningId);
        const currentInFuture = future.findIndex(p => p.id === this.currentPlanningId);

        if (currentInFuture !== -1) {
            this.current = future[currentInFuture];
            this.before = [...history];
            this.after = future.slice(currentInFuture + 1);
        } else if (currentInHistory !== -1) {
            this.current = history[currentInHistory];
            this.before = history.slice(currentInHistory + 1);
            this.after = [...future];
        } else {
            this.current = null;
            this.before = [];
            this.after = [...history].reverse().concat(future);
        }
    }

    // Toont max CAPACITY_PER_SIDE voor en na de huidige planning (21 in totaal met de
    // huidige erbij). Is er op één kant niet genoeg, dan schuift het tekort naar de
    // andere kant zodat we toch zo dicht mogelijk bij 21 uitkomen.
    private buildWindow() {
        let showBefore = Math.min(this.before.length, CAPACITY_PER_SIDE);
        let showAfter = Math.min(this.after.length, CAPACITY_PER_SIDE);

        if (showAfter < CAPACITY_PER_SIDE) {
            showBefore = Math.min(this.before.length, showBefore + (CAPACITY_PER_SIDE - showAfter));
        } else if (showBefore < CAPACITY_PER_SIDE) {
            showAfter = Math.min(this.after.length, showAfter + (CAPACITY_PER_SIDE - showBefore));
        }

        this.beforeItems = this.before.slice(0, showBefore).reverse();
        this.afterItems = this.after.slice(0, showAfter);
    }

    // Zoekt het hoogste aantal N waarbij zowel de N verste before- als de N verste
    // after-items volledig binnen het scherm vallen (getBoundingClientRect, niet
    // container-overflow: die hangt af van een keten van height:100%'s die niet
    // overal betrouwbaar resolvet). Toont dus altijd evenveel voor als na het
    // huidige item, tenzij een kant simpelweg minder items heeft. Het huidige item
    // zelf staat buiten .section-before/-after en wordt dus nooit verborgen.
    private fitToContainer() {
        const container = this.sectionItemsRef?.nativeElement;

        if (!container) {
            return;
        }

        const beforeEls = Array.from(container.querySelectorAll<HTMLElement>('.section-before > app-need-item'));
        const afterEls = Array.from(container.querySelectorAll<HTMLElement>('.section-after > app-need-item'));

        const fitsOnScreen = (el: HTMLElement) => {
            const rect = el.getBoundingClientRect();
            return rect.top >= 0 && rect.bottom <= window.innerHeight;
        };

        for (let n = Math.max(beforeEls.length, afterEls.length); n >= 0; n--) {
            const showBefore = Math.min(n, beforeEls.length);
            const showAfter = Math.min(n, afterEls.length);

            beforeEls.forEach((el, i) => (el.style.display = i < beforeEls.length - showBefore ? 'none' : ''));
            afterEls.forEach((el, i) => (el.style.display = i < showAfter ? '' : 'none'));

            const beforeFits = showBefore === 0 || fitsOnScreen(beforeEls[beforeEls.length - showBefore]);
            const afterFits = showAfter === 0 || fitsOnScreen(afterEls[showAfter - 1]);

            if (beforeFits && afterFits) {
                return;
            }
        }
    }
}
